import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

export const createExcelFileForSingleUserTest = (data) => {
  const { phoneNumber, govId, companyId } = data;
  const worksheet = XLSX.utils.aoa_to_sheet([
    ['phoneNumber', 'govId'], // Header row
    [phoneNumber, govId],
  ]);

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');

  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

  const blob = new Blob([excelBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });

  const fileName = `user_data_${new Date().toISOString().split('T')[0]}.xlsx`;

  const file = new File([blob], fileName, {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });

  return {
    file,
    fileName,
    companyId,
  };
};

export const prepareFormData = (file, companyId) => {
  const formData = new FormData();

  formData.append('file', file);
  formData.append('companyId', companyId);

  return formData;
};

export const validateDataHasContent = (data) => {
  return (
    Object.keys(data)?.length > 0 &&
    data.hasOwnProperty('registered') &&
    data.hasOwnProperty('notRegistered') &&
    data.hasOwnProperty('summary') &&
    data?.summary?.general?.totalCount > 0
  );
};

export const exportResultsToExcel = (results, companyId) => {
  const workbook = XLSX.utils.book_new();

  const summaryData = [
    ['סיכום כללי'],
    [''],
    ['סה"כ משתמשים', results.general.totalCount],
    ['רשומים', `${results.general.registeredCount} (${results.general.registeredPercentage}%)`],
    [
      'לא רשומים',
      `${results.general.notRegisteredCount} (${results.general.notRegisteredPercentage}%)`,
    ],
    [''],
    ['סיכום משתמשים רשומים'],
    [''],
    ['חלוקה לפי חברות:'],
  ];

  results.registeredSummary.companyData.forEach((company) => {
    summaryData.push([
      company.companyId === 'null' ? 'ללא חברה' : `חברה ${company.companyId}`,
      `${company.count} (${company.percentage}%)`,
    ]);
  });

  summaryData.push(['']);
  summaryData.push(['אינטגרציות:']);

  const failedIntegrationsOfRegistered = results.registeredSummary.integrationsData.find((item) =>
    item.hasOwnProperty('failedCount'),
  );

  summaryData.push([
    'נכשלו',
    `${failedIntegrationsOfRegistered?.failedCount ?? 0} (${failedIntegrationsOfRegistered?.failedPercentage ?? '0.00'}%)`,
  ]);

  summaryData.push([
    'עם שעות עבודה',
    `${results.registeredSummary.withWorkingHoursCount} (${results.registeredSummary.withWorkingHoursPercentage}%)`,
  ]);

  summaryData.push(['']);
  summaryData.push(['סיכום משתמשים לא רשומים']);
  summaryData.push(['']);
  summaryData.push(['חלוקה לפי מעסיקים:']);

  results.notRegisteredSummary.employerData.forEach((employer) => {
    summaryData.push([
      employer.employerId === 'null' ? 'ללא מעסיק' : `מעסיק ${employer.employerId}`,
      `${employer.count} (${employer.percentage}%)`,
    ]);
  });

  summaryData.push(['']);
  summaryData.push(['נתונים נוספים:']);

  const failedIntegrationsOfNotRegistered = results.registeredSummary.integrationsData.find(
    (item) => item.hasOwnProperty('failedCount'),
  );

  summaryData.push([
    'עם שגיאות',
    `${results.notRegisteredSummary.withErrorsCount} (${results.notRegisteredSummary.withErrorsPercentage}%)`,
  ]);

  summaryData.push([
    'עם שעות עבודה',
    `${results.notRegisteredSummary.withWorkingHoursCount} (${results.notRegisteredSummary.withWorkingHoursPercentage}%)`,
  ]);

  summaryData.push([
    'אינטגרציות נכשלו',
    `${failedIntegrationsOfNotRegistered?.failedCount ?? 0} (${failedIntegrationsOfNotRegistered?.failedPercentage ?? '0.00'}%)`,
  ]);

  const summaryWorksheet = XLSX.utils.aoa_to_sheet(summaryData);

  XLSX.utils.book_append_sheet(workbook, summaryWorksheet, 'סיכום');

  if (results.registered && results.registered.length > 0) {
    const registeredHeaders = [
      'מספר',
      'מספר טלפון',
      'שם מלא',
      'תעודת זהות',
      'חברה',
      'שכר שעתי',
      'אינטגרציה',
      'משמרות',
      'שגיאות',
    ];

    const registeredData = results.registered.map((user) => {
      const hourlyRate = user.hourlyGrossRate;
      const hourlyRateDisplay = hourlyRate && hourlyRate > 0 ?
                               `₪${hourlyRate.toFixed(2)}` :
                               'לא זמין';

      return [
        user.id,
        user.phoneNumber,
        user.fullName || '',
        user.details?.govId || '',
        user.companyId || '',
        hourlyRateDisplay,
        user.integrationData?.success ? 'הצליח' : 'נכשל',
        user.integrationData?.timeReporting?.length || 0,
        user.integrationData?.error || '',
      ];
    });

    const registeredWorksheet = XLSX.utils.aoa_to_sheet([registeredHeaders, ...registeredData]);

    XLSX.utils.book_append_sheet(workbook, registeredWorksheet, 'משתמשים רשומים');
  }

  if (results.notRegistered && results.notRegistered.length > 0) {
    const notRegisteredHeaders = [
      'טלפון נבדק',
      'טלפון בכספת',
      'תז נבדקת',
      'תז בכספת',
      'מעסיק',
      'סניף',
      'שם מלא',
      'שכר שעתי',
      'אינטגרציה',
      'משמרות',
      'שגיאות',
    ];

    const notRegisteredData = results.notRegistered.map((user) => {
      // Get hourly salary from multiple sources
      const hourlyRate = user.hourlyGrossRate ||
                        user.userDto?.details?.salaryDetails?.hourlyGrossRate ||
                        null;

      const hourlyRateDisplay = hourlyRate && hourlyRate > 0 ?
                               `₪${hourlyRate.toFixed(2)}` :
                               'לא זמין';

      return [
        user.phoneNumber || '',
        user.userDto?.phoneNumber || '',
        user.govId || '',
        user.userDto?.details?.govId || '',
        user.employerId || '',
        user.userDto?.details?.branch || '',
        user.userDto ? `${user.userDto.firstName || ''} ${user.userDto.lastName || ''}`.trim() : '',
        hourlyRateDisplay,
        user.integrationData?.success ? 'הצליח' : 'נכשל',
        user.integrationData?.timeReporting?.length || 0,
        user.errors?.join(', ') || '',
      ];
    });

    const notRegisteredWorksheet = XLSX.utils.aoa_to_sheet([
      notRegisteredHeaders,
      ...notRegisteredData,
    ]);

    XLSX.utils.book_append_sheet(workbook, notRegisteredWorksheet, 'משתמשים לא רשומים');
  }

  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

  const blob = new Blob([excelBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });

  const fileName = `test_results_company_${companyId}_${new Date().toISOString().split('T')[0]}.xlsx`;

  saveAs(blob, fileName);
};
